/**
 * EMT consent to be contacted by training institutes about renewing a certification.
 *
 * OFF by default: nobody is opted in unless they turn it on themselves (existing EMTs included).
 * Stored on the EMT's own profile — profiles/{uid}.preferences.privacy.institutesMayContact — which
 * firestore.rules keeps owner-only, so no institute can read it (or anything else on the profile).
 * Institutes only ever get renewal leads through functions/src/institutes.ts, which reads this flag
 * from the profile on every request (and again before every message), so turning it off takes effect
 * on the very next request — there is no copy of the consent anywhere else to go stale.
 *
 * Synced verbatim into functions/src/generated/ (scripts/syncFunctionsGenerated.mjs).
 */

/** Field under profiles/{uid}.preferences.privacy. */
export const INSTITUTE_CONTACT_PREF_KEY = 'institutesMayContact';
/** Full Firestore field path, for server queries and updates. */
export const INSTITUTE_CONTACT_FIELD_PATH = 'preferences.privacy.institutesMayContact';

export function isInstituteContactOptedIn(profile: unknown): boolean {
  const prefs = (profile as { preferences?: { privacy?: Record<string, unknown> } } | null | undefined)?.preferences;
  return prefs?.privacy?.[INSTITUTE_CONTACT_PREF_KEY] === true;
}

/** Plain-language consent copy — what is shared, with whom, and how to take it back. */
export const INSTITUTE_CONTACT_CONSENT = {
  label: 'Let training institutes near me that Arevina has reviewed contact me about renewing my certifications',
  shared:
    'When this is on, training institutes that Arevina has reviewed, that teach a certification of yours ' +
    'expiring within the next 90 days, and that are near you can see your name, which certification is ' +
    'expiring, and whether it expires within 30, 60 or 90 days.',
  notShared:
    'They never see your email, phone number, documents, exact expiration date, work history or the rest ' +
    'of your profile, and they cannot see you at all while this is off.',
  revoke: 'Turn it off any time: you disappear from their lists immediately.',
} as const;

/** In-app notification type for an institute's renewal message. */
export const INSTITUTE_OUTREACH_NOTIFICATION_TYPE = 'institute_outreach';

/** featureFlags/global flag key. Outreach is refused server-side unless its state is 'on'. */
export const INSTITUTE_OUTREACH_FLAG = 'institute_outreach';

/** Per-institute limits, enforced by sendInstituteOutreach. */
export const INSTITUTE_OUTREACH_LIMITS = {
  /** Recipients in one send. */
  perSend: 25,
  /** Recipients per institute per UTC day. */
  perDay: 50,
  /** The same institute may message the same EMT at most once in this many days. */
  perEmtCooldownDays: 30,
  /** Message body length. */
  maxMessageChars: 1000,
} as const;

/** SPA page that turns the consent off (from an email link with a token, or signed in). */
export const INSTITUTE_UNSUBSCRIBE_PATH = '/unsubscribe/institutes';
